import React, { useEffect, useState } from 'react';
import { fetchCertificateRangeAdmin, saveCertificateRange } from './certificateNumberService';

// Mirrors the server-side format: digits with optional capital letters and
// - or / separators around them.
const toCertificateInput = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9/-]/g, '');

const CalorCertificateRangeModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [disableForTesting, setDisableForTesting] = useState(false);
  const [loadedDisableForTesting, setLoadedDisableForTesting] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');
    setSuccess('');
    setConfirmDisableOpen(false);

    fetchCertificateRangeAdmin()
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        setRangeStart(data.rangeStart != null ? String(data.rangeStart) : '');
        setRangeEnd(data.rangeEnd != null ? String(data.rangeEnd) : '');
        const disabled = data.numberingDisabled === true;
        setDisableForTesting(disabled);
        setLoadedDisableForTesting(disabled);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load certificate range.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const rangeStats = (() => {
    if (!status?.rangeConfigured || status.rangeStartNumber == null || status.rangeEndNumber == null) return null;
    const totalInRange = status.totalInRange ?? (status.rangeEndNumber - status.rangeStartNumber + 1);
    const pointer = status.nextNumberPointer ?? (status.rangeEndNumber + 1);
    const issuedCount = status.issuedCount ?? Math.min(totalInRange, Math.max(0, pointer - status.rangeStartNumber));
    const positionInRange = status.positionInRange ?? (
      status.available && status.nextNumber ? issuedCount + 1 : totalInRange
    );
    const remaining = status.remaining ?? Math.max(0, status.rangeEndNumber - pointer + 1);
    return { totalInRange, issuedCount, positionInRange, remaining };
  })();

  const persistChanges = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        numberingDisabledForTesting: disableForTesting,
      };

      if (!disableForTesting || (rangeStart !== '' && rangeEnd !== '')) {
        payload.rangeStart = rangeStart.trim();
        payload.rangeEnd = rangeEnd.trim();
      }

      const data = await saveCertificateRange(payload);
      setStatus(data);
      setLoadedDisableForTesting(data.numberingDisabled === true);
      setDisableForTesting(data.numberingDisabled === true);
      if (data.numberingDisabled) {
        setSuccess('Certificate numbering disabled for testing. Users must enter certificate numbers manually.');
      } else {
        setSuccess('Certificate number range saved. The next certificate will use the start of the new range.');
      }
      setConfirmDisableOpen(false);
    } catch (err) {
      setError(err.message || 'Could not save certificate range.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (event) => {
    event.preventDefault();

    if (disableForTesting && !loadedDisableForTesting) {
      setConfirmDisableOpen(true);
      return;
    }

    persistChanges();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-70" onClick={onClose} />
        <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
          <div className="px-6 py-4 border-b border-pvs-border/60">
            <h2 className="text-white text-lg font-bold">CDE Certificate Numbers</h2>
            <p className="text-pvs-text-muted text-sm mt-1">
              Set the allocated certificate number range for Calor CDE submissions.
            </p>
          </div>

          <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
            {loading ? (
              <p className="text-pvs-text-muted text-sm">Loading current range…</p>
            ) : (
              <>
                {status?.rangeConfigured && (
                  <div className="pvs-glass border border-pvs-border rounded-lg p-4 space-y-3">
                    <div className="text-xs uppercase tracking-wide text-pvs-text-muted">Current allocation</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white">
                      <div>
                        <div className="text-pvs-text-muted text-xs">Configured range</div>
                        <div className="font-semibold text-base mt-0.5">
                          {status.rangeStart} – {status.rangeEnd}
                          {rangeStats && (
                            <span className="text-pvs-text-muted font-normal text-sm">
                              {' '}({rangeStats.totalInRange} numbers)
                            </span>
                          )}
                        </div>
                      </div>
                      {!disableForTesting ? (
                        <div>
                          <div className="text-pvs-text-muted text-xs">Progress</div>
                          {status.available && status.nextNumber ? (
                            <div className="font-semibold text-base mt-0.5">
                              Next: {status.nextNumber}
                              {rangeStats && (
                                <span className="text-pvs-accent font-normal text-sm">
                                  {' '}({rangeStats.positionInRange} of {rangeStats.totalInRange})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="font-semibold text-base mt-0.5 text-red-300">
                              Range exhausted
                              {rangeStats && (
                                <span className="font-normal text-sm">
                                  {' '}({rangeStats.totalInRange} of {rangeStats.totalInRange} used)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="text-pvs-text-muted text-xs">Progress</div>
                          <div className="font-semibold text-base mt-0.5 text-amber-200">
                            Not tracked (testing mode)
                          </div>
                        </div>
                      )}
                    </div>
                    {!disableForTesting && rangeStats && rangeStats.totalInRange > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-pvs-text-muted mb-1">
                          <span>{rangeStats.issuedCount} issued</span>
                          <span>{rangeStats.remaining} remaining</span>
                        </div>
                        <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              status.available ? 'bg-pvs-accent' : 'bg-red-500'
                            }`}
                            style={{
                              width: `${Math.min(100, Math.round((rangeStats.issuedCount / rangeStats.totalInRange) * 100))}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {status.configuredByUsername && (
                      <div className="text-pvs-text-muted text-xs pt-1 border-t border-pvs-border/40">
                        Last updated by {status.configuredByUsername}
                      </div>
                    )}
                  </div>
                )}

                <label className="flex items-start gap-3 pvs-glass border border-amber-500/30 bg-amber-900/10 rounded-lg p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={disableForTesting}
                    onChange={(e) => setDisableForTesting(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-pvs-border"
                  />
                  <span>
                    <span className="block text-sm font-medium text-white">
                      Disable certificate numbering (testing only)
                    </span>
                    <span className="block text-xs text-pvs-text-muted mt-1 leading-relaxed">
                      Bypasses range allocation so the CDE form can be tested with manually entered certificate numbers.
                    </span>
                  </span>
                </label>

                <div className={`grid grid-cols-2 gap-4${disableForTesting ? ' opacity-60' : ''}`}>
                  <label className="block">
                    <span className="text-sm text-pvs-text-muted">Range start</span>
                    <input
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      placeholder="e.g. CGL0100"
                      required={!disableForTesting}
                      disabled={disableForTesting}
                      value={rangeStart}
                      onChange={(e) => setRangeStart(toCertificateInput(e.target.value))}
                      className="mt-1 w-full rounded-lg border border-pvs-border bg-black/20 px-3 py-2 text-white uppercase disabled:cursor-not-allowed"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-pvs-text-muted">Range end</span>
                    <input
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      placeholder="e.g. CGL0500"
                      required={!disableForTesting}
                      disabled={disableForTesting}
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(toCertificateInput(e.target.value))}
                      className="mt-1 w-full rounded-lg border border-pvs-border bg-black/20 px-3 py-2 text-white uppercase disabled:cursor-not-allowed"
                    />
                  </label>
                </div>

                <p className="text-pvs-text-dim text-xs leading-relaxed">
                  Numbers may include capital letters, for example CGL0100 to CGL0500. The letters stay fixed and
                  only the digits increment; leading zeros are kept.
                </p>

                {disableForTesting && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-200">
                    Testing mode is active. Certificate numbers will not be allocated automatically and the configured range will not be enforced.
                  </div>
                )}

                <p className="text-pvs-text-dim text-xs leading-relaxed">
                  Saving a new range resets allocation to the start value. Certificate numbers are issued only on successful CDE form submission and cannot be reused.
                </p>
              </>
            )}

            {error && (
              <div className="rounded-lg border border-red-500/40 bg-red-900/20 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-lg border border-green-500/40 bg-green-900/20 px-4 py-3 text-sm text-green-300">
                {success}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading || saving}
                className="px-5 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {confirmDisableOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-80" />
          <div className="relative pvs-glass border border-amber-500/40 rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-600 px-6 py-4">
              <h3 className="text-white text-lg font-bold">Disable certificate numbering?</h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-white text-sm leading-relaxed">
                Are you sure you want to disable certificate numbering?
              </p>
              <p className="text-pvs-text-muted text-sm leading-relaxed">
                The configured range will not be enforced and users must enter certificate numbers manually on the CDE form. This setting is intended for testing only and should be turned off before live use.
              </p>
            </div>
            <div className="px-6 pb-5 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmDisableOpen(false)}
                disabled={saving}
                className="px-4 py-2.5 border border-pvs-border text-white rounded-lg hover:bg-white/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={persistChanges}
                disabled={saving}
                className="px-5 py-2.5 bg-amber-500 text-pvs-bg font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Yes, disable numbering'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalorCertificateRangeModal;
